import { io, Socket } from 'socket.io-client';
import { 
  ExtractionRequest, 
  ExtractionResponse, 
  ExtractionProgressData, 
  ConnectionStatus,
  SocketError 
} from '../types';

export class ICD10SocketService {
  private socket: Socket | null = null;
  private connectionStatus: ConnectionStatus = ConnectionStatus.DISCONNECTED;
  private statusCallbacks: Array<(status: ConnectionStatus) => void> = [];
  private progressCallbacks: Array<(data: ExtractionProgressData) => void> = [];
  private resultCallbacks: Array<(data: ExtractionResponse) => void> = [];
  private errorCallbacks: Array<(error: SocketError) => void> = [];
  private titleFlashInterval: number | null = null;
  private titleFlashTimeout: number | null = null;
  private originalTitle: string | null = null;
  private visibilityHandler: (() => void) | null = null;
  private soundEnabled: boolean = true;
  private activeRequestsKey = 'icd10:activeRequests';
  // Reconnect / backoff management
  private reconnectAttempts = 0;
  private reconnectTimer: number | null = null;
  private manualDisconnect = false; // flag to not auto-reconnect when user/auth intentionally disconnects
  private readonly MAX_RECONNECT_ATTEMPTS = 12; // ~ will cap exponential growth
  private readonly BASE_RECONNECT_DELAY_MS = 750; // starting delay
  private readonly MAX_RECONNECT_DELAY_MS = 15000; // cap delay
  private readonly BACKOFF_MULTIPLIER = 2; // exponential factor
  private readonly JITTER_RATIO = 0.2; // +/-20% jitter

  // Prefer the Vite dev server proxy (`/api`) when running in development.
  // In production the backend URL can be provided via `VITE_SOCKET_URL`.
  constructor(private serverUrl: string = (import.meta.env as any).DEV ? '/api' : (import.meta.env.VITE_SOCKET_URL || '/api')) {}

  connect(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.socket?.connected) {
        resolve();
        return;
      }

      this.setConnectionStatus(ConnectionStatus.CONNECTING);
      this.manualDisconnect = false;

      // When using the Vite proxy the client should connect using a relative
      // URL and specify the socket.io path used by the proxy: `/api/socket.io`.
      // Also derive the transport scheme from the current page (wss vs ws)
      // so secure connections work when served over HTTPS.
      const isRelative = this.serverUrl.startsWith('/');
      const token = (typeof window !== 'undefined') ? window.localStorage.getItem('bearer_token') : null;
      const socketOptions: any = {
        transports: ['websocket', 'polling'],
        timeout: 10000,
        reconnection: false, // we will manage reconnection manually for clearer backoff control
        upgrade: true,
        forceNew: false,
        auth: token ? { token } : undefined,
      };

      if (isRelative) {
        // Use relative URL with explicit path for socket.io
        // The server is configured to handle Socket.IO at /api/socket.io/
        socketOptions.path = `${this.serverUrl.replace(/\/$/, '')}/socket.io/`;
        this.socket = io(undefined as any, socketOptions);
      } else {
        // Full absolute backend URL provided (e.g., in production)
        this.socket = io(this.serverUrl, socketOptions);
      }

      this.socket.on('connect', () => {
        console.log('Connected to ICD10 server');
        // Reset backoff state
        this.reconnectAttempts = 0;
        if (this.reconnectTimer) {
          clearTimeout(this.reconnectTimer);
          this.reconnectTimer = null;
        }
        this.setConnectionStatus(ConnectionStatus.CONNECTED);
        resolve();
      });

      this.socket.on('disconnect', (reason) => {
        console.log('Disconnected from ICD10 server:', reason);
        this.setConnectionStatus(ConnectionStatus.DISCONNECTED);
        if (!this.manualDisconnect && reason !== 'io client disconnect') {
          this.scheduleReconnect();
        }
      });

      this.socket.on('connect_error', (error) => {
        const errorData: any = (error as any)?.data || (error as any)?.context || (error as any)?.payload;
        const errorCode = errorData?.error_code || errorData?.code || (error as any)?.code;
        const statusCode = errorData?.status_code || (error as any)?.status;
        const errorMessage = errorData?.user_message || errorData?.message || (error as any)?.message;
        const isAuthRequired = errorCode === 'auth_required' || statusCode === 401 || /auth required/i.test(errorMessage || '');

        console.error('Connection error:', error);
        this.setConnectionStatus(ConnectionStatus.ERROR);
        this.notifyError({
          message: isAuthRequired ? (errorMessage || 'Authentication required') : `Connection failed: ${error.message}`,
          code: isAuthRequired ? 'AUTH_REQUIRED' : 'CONNECTION_ERROR',
          timestamp: new Date().toISOString()
        });
        if (isAuthRequired) {
          this.manualDisconnect = true;
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          try { this.socket?.disconnect(); } catch (_) {}
          return;
        }
        // Only reject the initial promise for the very first attempt
        if (this.reconnectAttempts === 0) {
          reject(error);
        }
        this.scheduleReconnect();
      });

      this.socket.on('connection_error', (error) => {
        console.error('Server connection error:', error);
        const errorData: any = (error as any)?.data || (error as any)?.context || (error as any)?.payload;
        const errorCode = error?.code || errorData?.error_code || errorData?.code;
        const statusCode = errorData?.status_code || (error as any)?.status;
        const errorMessage = errorData?.user_message || errorData?.message || error?.error;
        const isAuthRequired = errorCode === 'auth_required' || statusCode === 401 || /auth required/i.test(errorMessage || '');
        this.setConnectionStatus(ConnectionStatus.ERROR);
        this.notifyError({
          message: isAuthRequired ? (errorMessage || 'Authentication required') : (error.error || 'Server connection error'),
          code: isAuthRequired ? 'AUTH_REQUIRED' : (error.code || 'SERVER_ERROR'),
          timestamp: new Date().toISOString()
        });
        if (isAuthRequired) {
          this.manualDisconnect = true;
          if (this.reconnectTimer) {
            clearTimeout(this.reconnectTimer);
            this.reconnectTimer = null;
          }
          try { this.socket?.disconnect(); } catch (_) {}
        }
      });

      // We manage reconnection ourselves; no built-in reconnection events used.

      // Listen for extraction progress updates
      this.socket.on('extraction_progress', (data: ExtractionProgressData) => {
        console.log('Extraction progress:', data);
        this.notifyProgress(data);
      });

      // Listen for extraction results
      this.socket.on('extraction_complete', (data: ExtractionResponse) => {
        console.log('Extraction completed:', data);
        this.notifyResult(data);
        // If extraction includes a clientRequestId, remove it from pending
        try {
          const anyData: any = data as any;
          if (anyData && anyData.clientRequestId) {
            this.removePendingRequest(anyData.clientRequestId);
          }
        } catch (_) {
          // ignore
        }
        // Play a short notification sound to indicate completion (respects user setting)
        try {
          this.playSound();
          this.startTitleFlash('💯 Extraction complete');
        } catch (e) {
          // ignore audio errors in environments where audio isn't available
          console.warn('Exception when playing sound/changing title:', e);
        }
      });

      // Listen for errors
      this.socket.on('extraction_error', (error: any) => {
        console.error('Extraction error:', error);
        const message =
          error?.user_message
          || error?.message
          || error?.detail
          || 'Unknown extraction error';
        this.notifyError({
          message,
          code: error.code || 'EXTRACTION_ERROR',
          timestamp: new Date().toISOString()
        });
      });

      // Set connection timeout
      setTimeout(() => {
        if (this.connectionStatus === ConnectionStatus.CONNECTING) {
          this.setConnectionStatus(ConnectionStatus.ERROR);
          reject(new Error('Connection timeout'));
        }
      }, 10000);
    });
  }

  disconnect(): void {
    this.manualDisconnect = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.socket) {
      try { this.socket.disconnect(); } catch (_) {}
      this.socket = null;
    }
    this.reconnectAttempts = 0;
    this.setConnectionStatus(ConnectionStatus.DISCONNECTED);
  }

  extractCodes(request: ExtractionRequest): void {
    if (!this.socket?.connected) {
      throw new Error('Not connected to server');
    }

    console.log('Sending extraction request:', request);
    // Ensure each request has a clientRequestId so it can be resumed after refresh
    const clientRequestId = this.generateClientRequestId();

    const resolvedLocale =
      request.locale
      || ((): string | null => {
        try {
          return typeof window !== 'undefined' ? window.localStorage.getItem('locale') : null;
        } catch (_) {
          return null;
        }
      })()
      || (typeof navigator !== 'undefined' ? navigator.language : null)
      || 'en';

    const payload = {
      ...request,
      streaming_callback: true, // Always enable streaming for UI
      clientRequestId,
      locale: resolvedLocale,
    } as any;

    // Persist the pending request so it can be resumed on reconnect/refresh
    try {
      this.persistPendingRequest(payload);
    } catch (_) {
      // ignore
    }

    this.socket.emit('extract_codes_stream', payload);
  }

  // -------- Reconnection / Backoff Helpers --------
  private computeBackoffDelay(): number {
    const attempt = Math.min(this.reconnectAttempts, this.MAX_RECONNECT_ATTEMPTS);
    const expo = this.BASE_RECONNECT_DELAY_MS * Math.pow(this.BACKOFF_MULTIPLIER, attempt);
    const capped = Math.min(expo, this.MAX_RECONNECT_DELAY_MS);
    const jitterRange = capped * this.JITTER_RATIO;
    const jitter = (Math.random() * jitterRange * 2) - jitterRange; // +/- jitterRange
    return Math.max(250, Math.round(capped + jitter));
  }

  private scheduleReconnect() {
    if (this.manualDisconnect) return;
    if (this.socket?.connected) return;
    if (this.reconnectAttempts >= this.MAX_RECONNECT_ATTEMPTS) {
      console.warn('Max reconnect attempts reached; giving up.');
      this.notifyError({
        message: 'Unable to reconnect to server (max attempts reached)',
        code: 'RECONNECT_GIVE_UP',
        timestamp: new Date().toISOString()
      });
      return;
    }
    if (this.reconnectTimer) return; // already scheduled
    this.reconnectAttempts += 1;
    const delay = this.computeBackoffDelay();
    console.log(`Scheduling reconnect attempt #${this.reconnectAttempts} in ${delay}ms`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      // Re-create or reconnect socket
      if (this.socket) {
        try {
          this.setConnectionStatus(ConnectionStatus.CONNECTING);
          this.socket.connect();
        } catch (e) {
          console.error('Reconnect connect() error:', e);
          this.scheduleReconnect();
        }
      } else {
        // If socket disappeared, start fresh
        void this.connect().catch(() => { /* initial promise errors handled inside */ });
      }
    }, delay);
  }

  // Simple client-side id generator — avoids external dependency
  private generateClientRequestId(): string {
    try {
      // Use crypto.randomUUID if available
      const globalObj: any = (typeof globalThis !== 'undefined' ? globalThis : {});
      if (globalObj.crypto && typeof globalObj.crypto.randomUUID === 'function') {
        return globalObj.crypto.randomUUID();
      }
    } catch (_) {
      // ignore
    }
    // Fallback to timestamp + random
    return `cid-${Date.now()}-${Math.floor(Math.random() * 0xffff).toString(16)}`;
  }

  // Status management
  getConnectionStatus(): ConnectionStatus {
    return this.connectionStatus;
  }

  private setConnectionStatus(status: ConnectionStatus): void {
    this.connectionStatus = status;
    this.statusCallbacks.forEach(callback => callback(status));
  }

  // Event subscription methods
  onStatusChange(callback: (status: ConnectionStatus) => void): () => void {
    this.statusCallbacks.push(callback);
    return () => {
      const index = this.statusCallbacks.indexOf(callback);
      if (index > -1) {
        this.statusCallbacks.splice(index, 1);
      }
    };
  }

  onProgress(callback: (data: ExtractionProgressData) => void): () => void {
    this.progressCallbacks.push(callback);
    return () => {
      const index = this.progressCallbacks.indexOf(callback);
      if (index > -1) {
        this.progressCallbacks.splice(index, 1);
      }
    };
  }

  onResult(callback: (data: ExtractionResponse) => void): () => void {
    this.resultCallbacks.push(callback);
    return () => {
      const index = this.resultCallbacks.indexOf(callback);
      if (index > -1) {
        this.resultCallbacks.splice(index, 1);
      }
    };
  }

  onError(callback: (error: SocketError) => void): () => void {
    this.errorCallbacks.push(callback);
    return () => {
      const index = this.errorCallbacks.indexOf(callback);
      if (index > -1) {
        this.errorCallbacks.splice(index, 1);
      }
    };
  }

  // Private notification methods
  private notifyProgress(data: ExtractionProgressData): void {
    this.progressCallbacks.forEach(callback => callback(data));
  }

  private notifyResult(data: ExtractionResponse): void {
    this.resultCallbacks.forEach(callback => callback(data));
  }

  private persistResult(data: ExtractionResponse, originalText?: string): void {
    try {
      if (typeof window === 'undefined') return;
      const key = 'icd10:lastResults';
      const raw = window.localStorage.getItem(key);
      // allow storing optional originalText
      type SavedExt = { data: ExtractionResponse; savedAt: string; originalText?: string };
      const arr: SavedExt[] = raw ? JSON.parse(raw) as SavedExt[] : [];
      const entry: SavedExt = { data, savedAt: new Date().toISOString() };
      if (originalText) entry.originalText = originalText;
      arr.unshift(entry);
      const trimmed = arr.slice(0, 20);
      window.localStorage.setItem(key, JSON.stringify(trimmed));
    } catch (e) {
      // ignore storage errors to avoid breaking the service
    }
  }

  // Public helper to save a result along with the original input text (if available)
  public saveResultWithText(data: ExtractionResponse, originalText?: string): void {
    try {
      this.persistResult(data, originalText);
    } catch (e) {
      // ignore
    }
  }

  // Public helpers to access persisted recent results
  public getLastResults(): Array<{ data: ExtractionResponse; savedAt: string; originalText?: string }> {
    try {
      if (typeof window === 'undefined') return [];
      const raw = window.localStorage.getItem('icd10:lastResults');
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  public clearLastResults(): void {
    try {
      if (typeof window === 'undefined') return;
      window.localStorage.removeItem('icd10:lastResults');
    } catch (e) {
      // ignore
    }
  }

  private notifyError(error: SocketError): void {
    this.errorCallbacks.forEach(callback => callback(error));
  }

  // Utility methods
  isConnected(): boolean {
    return this.socket?.connected || false;
  }

  getSocketId(): string | undefined {
    return this.socket?.id;
  }

  // Play a short beep using the Web Audio API. Silent-fails if not available.
  private playNotificationSound(): void {
    // Try playing a bundled audio file first (served from `public/notification_sound.mp3`)
    try {
      if (typeof window !== 'undefined' && typeof Audio !== 'undefined') {
        const audio = new Audio('/notification_sound.mp3');
        audio.volume = 0.8;
        // Try to play; browsers may block autoplay until user interaction.
        const playPromise = audio.play();
        if (playPromise && typeof playPromise.then === 'function') {
          playPromise.catch(() => {
            // If playback fails (autoplay blocked), fallback to WebAudio short beep
            this.playFallbackBeep();
          });
        }
        return;
      }
    } catch (err) {
      // fall through to fallback
    }

    // Fallback to a short WebAudio beep
    this.playFallbackBeep();
  }

  // Public sound controls
  setSoundEnabled(enabled: boolean): void {
    this.soundEnabled = !!enabled;
  }

  isSoundEnabled(): boolean {
    return this.soundEnabled;
  }

  // Public trigger that respects the enabled flag
  playSound(): void {
    if (!this.soundEnabled) return;
    try {
      this.playNotificationSound();
    } catch (_) {
      // ignore
    }
  }

  // --- Pending request persistence for resume after refresh ---
  private persistPendingRequest(request: any): void {
    try {
      if (typeof window === 'undefined') return;
      const raw = window.localStorage.getItem(this.activeRequestsKey);
      const arr = raw ? JSON.parse(raw) as any[] : [];
      arr.push(request);
      window.localStorage.setItem(this.activeRequestsKey, JSON.stringify(arr));
    } catch (_) {
      // ignore
    }
  }

  private removePendingRequest(clientRequestId: string): void {
    try {
      if (typeof window === 'undefined') return;
      const raw = window.localStorage.getItem(this.activeRequestsKey);
      if (!raw) return;
      const arr = JSON.parse(raw) as any[];
      const filtered = arr.filter(r => r.clientRequestId !== clientRequestId);
      window.localStorage.setItem(this.activeRequestsKey, JSON.stringify(filtered));
    } catch (_) {
      // ignore
    }
  }

  private playFallbackBeep(): void {
    try {
      const globalObj: any = (typeof globalThis !== 'undefined' ? globalThis : {});
      const AudioCtx = globalObj.AudioContext || globalObj.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const o = ctx.createOscillator();
      const gain = ctx.createGain();
      o.type = 'sine';
      o.frequency.value = 880; // A5
      gain.gain.value = 0.05; // low volume
      o.connect(gain);
      gain.connect(ctx.destination);
      o.start();
      setTimeout(() => {
        o.stop();
        try { ctx.close(); } catch (_) { /* ignore */ }
      }, 150);
    } catch (err) {
      // ignore any errors to avoid breaking the service
    }
  }

  // Flash the document title intermittently; stops when tab visible or after duration
  private startTitleFlash(message: string, durationMs = 80000, periodMs = 700): void {
    try {
      if (typeof document === 'undefined' || typeof window === 'undefined') return;

      // If already flashing, stop previous first
      this.stopTitleFlash();

      // If the page is currently focused (visible and focused), don't flash
      // Use document.hasFocus() which is more specific than visibility alone.
      try {
        if (typeof document !== 'undefined' && document.hasFocus && document.hasFocus()) {
          return;
        }
      } catch (_) {
        // ignore and continue if any exception occurs
      }

      // Optionally only flash when the page is hidden
      // If the tab is visible, this will be a short single reminder
      this.originalTitle = document.title;
      let showingMessage = false;
      const toggle = () => {
        showingMessage = !showingMessage;
        document.title = showingMessage ? message : (this.originalTitle ?? message);
      };

      // Start toggling
      toggle();
      this.titleFlashInterval = window.setInterval(toggle, periodMs);

      // Stop after duration
      this.titleFlashTimeout = window.setTimeout(() => this.stopTitleFlash(), durationMs);

      // Stop immediately when the tab becomes visible
      this.visibilityHandler = () => {
        if (!document.hidden) {
          this.stopTitleFlash();
        }
      };
      document.addEventListener('visibilitychange', this.visibilityHandler);
    } catch (_) {
      // no-op
    }
  }

  private stopTitleFlash(): void {
    try {
      if (typeof document === 'undefined' || typeof window === 'undefined') return;
      if (this.titleFlashInterval !== null) {
        window.clearInterval(this.titleFlashInterval);
        this.titleFlashInterval = null;
      }
      if (this.titleFlashTimeout !== null) {
        window.clearTimeout(this.titleFlashTimeout);
        this.titleFlashTimeout = null;
      }
      if (this.visibilityHandler) {
        document.removeEventListener('visibilitychange', this.visibilityHandler);
        this.visibilityHandler = null;
      }
      if (this.originalTitle !== null) {
        document.title = this.originalTitle;
        this.originalTitle = null;
      }
    } catch (_) {
      // no-op
    }
  }
}

// Create a singleton instance
export const socketService = new ICD10SocketService();
